import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { QueryBuilder, Rule } from 'react-querybuilder';
import 'react-querybuilder/dist/query-builder.css';
import { Checkbox, Input, Modal, Space, Spin, Typography } from 'antd';
import { HomeOutlined, SunOutlined, ThunderboltOutlined } from '@ant-design/icons';
import {
  ELIGIBILITY_FIELDS, FACETABLE_FIELDS, predicateToRqbQuery, rqbQueryToPredicate,
} from '../../services/eligibilityRuleSetsApi';
import EligibilityValueEditor from './EligibilityValueEditor';
import '../../styles/eligibility-rule-sets.css';

const QB_CONTROL_ELEMENTS = { valueEditor: EligibilityValueEditor };

// A rule set applies to any non-empty subset of these three slots (migration
// 176) -- never zero, and there's no single value meaning "all three" other
// than selecting all three checkboxes.
export const SLOT_OPTIONS = [
  { value: 'indoor', label: 'Indoor', icon: <HomeOutlined /> },
  { value: 'outdoor', label: 'Outdoor', icon: <SunOutlined /> },
  { value: 'furnace', label: 'Furnace', icon: <ThunderboltOutlined /> },
];

const QB_TRANSLATIONS = {
  addRule: { label: '+ Condition' },
  addGroup: { label: '+ Group' },
  notToggle: { label: 'NOT' },
};

const emptyQuery = () => ({ combinator: 'and', rules: [] });

// Shared query-builder modal for creating/editing a named eligibility rule
// set. Used by both the dedicated rule-sets management page and inline from
// the Customer Eligibility page (so admins don't have to leave the customer
// screen to build a new filter). `api` must provide getFacetValues(field,
// unitType) -- see services/eligibilityRuleSetsApi.js.
const EligibilityRuleSetEditor = ({ open, initial, defaultUnitType, api, onCancel, onSave, saving }) => {
  const [name, setName] = useState('');
  const [unitTypes, setUnitTypes] = useState(defaultUnitType ? [defaultUnitType] : ['indoor']);
  const [query, setQuery] = useState(emptyQuery());
  const [facetValues, setFacetValues] = useState({});
  const [facetsLoading, setFacetsLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(initial?.name || '');
    setUnitTypes(initial?.unit_types?.length ? initial.unit_types : (defaultUnitType ? [defaultUnitType] : ['indoor']));
    setQuery(initial?.predicate ? predicateToRqbQuery(initial.predicate) : emptyQuery());
  }, [open, initial, defaultUnitType]);

  // Re-fetch whenever the modal opens or the selected slots change -- facet
  // values are denormalized separately per indoor/outdoor/furnace slot, so
  // this fetches each selected slot and merges, summing counts for values
  // present on more than one slot. There's no "multi-slot" facet endpoint --
  // the backend only knows concrete, single slots.
  useEffect(() => {
    if (!open || !api?.getFacetValues || !unitTypes.length) return;
    let cancelled = false;
    setFacetsLoading(true);
    Promise.all(
      FACETABLE_FIELDS.map((field) =>
        Promise.all(
          unitTypes.map((slot) =>
            api
              .getFacetValues(field, slot)
              .then((data) => data.values || [])
              .catch(() => [])
          )
        ).then((valuesPerSlot) => {
          const merged = new Map();
          valuesPerSlot.flat().forEach((v) => {
            const existing = merged.get(v.value);
            merged.set(v.value, { value: v.value, count: (existing?.count || 0) + (v.count || 0) });
          });
          return [field, Array.from(merged.values())];
        })
      )
    ).then((pairs) => {
      if (cancelled) return;
      setFacetValues(Object.fromEntries(pairs));
      setFacetsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [open, unitTypes, api]);

  // Exact-match operators get a select/multiselect of real values once
  // loaded; substring operators (contains/starts_with/...) keep free text
  // since a fixed value list doesn't make sense for a partial match.
  const getValueEditorType = useCallback(
    (field, operator) => {
      if (!FACETABLE_FIELDS.includes(field)) return undefined;
      const hasValues = (facetValues[field] || []).length > 0;
      if ((operator === 'in' || operator === 'notIn') && hasValues) return 'multiselect';
      if ((operator === 'eq' || operator === 'ne') && hasValues) return 'select';
      return undefined;
    },
    [facetValues]
  );

  const getValues = useCallback(
    (field) => (facetValues[field] || []).map((v) => ({ name: v.value, label: v.count ? `${v.value} (${v.count})` : v.value })),
    [facetValues]
  );

  const hasConditions = query.rules.length > 0;

  // Every condition in this rule set is rendered against every selected slot
  // uniformly (predicate_to_solr_fq re-renders the same tree once per slot in
  // unit_types, substituting the field prefix -- see
  // customer_eligibility_predicate.py) -- there's no per-condition slot
  // scoping. So each rule row gets the same badges, mirroring the "Applies
  // to" checkboxes above, so which slots a condition will be evaluated
  // against is visible right next to it instead of requiring a scroll back up.
  const RuleWithSlotBadges = useMemo(() => {
    const RuleRow = (props) => (
      <div className="eligibility-rule-row">
        <Rule {...props} />
        <Space size={4} className="eligibility-rule-slot-badges">
          {unitTypes.map((slot) => {
            const opt = SLOT_OPTIONS.find((o) => o.value === slot);
            return (
              <span key={slot} className={`unit-tag unit-tag--${slot}`} title={`Applied to ${opt?.label || slot}`}>
                {opt?.label || slot}
              </span>
            );
          })}
        </Space>
      </div>
    );
    return RuleRow;
  }, [unitTypes]);

  const controlElements = useMemo(
    () => ({ ...QB_CONTROL_ELEMENTS, rule: RuleWithSlotBadges }),
    [RuleWithSlotBadges]
  );

  return (
    <Modal
      title={initial ? `Edit rule set: ${initial.name}` : 'New rule set'}
      open={open}
      onCancel={onCancel}
      width="min(860px, 94vw)"
      confirmLoading={saving}
      onOk={() => onSave({ name, unit_types: unitTypes, predicate: rqbQueryToPredicate(query) })}
      okButtonProps={{ disabled: !name.trim() || !hasConditions || !unitTypes.length }}
      okText="Save"
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: '100%' }} size={16}>
        <Space wrap size={20} align="start" style={{ width: '100%' }}>
          <div>
            <Typography.Text strong style={{ display: 'block', marginBottom: 6, fontSize: 12.5 }}>
              Rule set name
            </Typography.Text>
            <Input
              placeholder="e.g. Residential Carrier/Bryant only"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{ width: 300 }}
            />
          </div>
          <div>
            <Typography.Text strong style={{ display: 'block', marginBottom: 6, fontSize: 12.5 }}>
              Applies to
            </Typography.Text>
            <Checkbox.Group
              className="eligibility-slot-checkboxes"
              value={unitTypes}
              onChange={setUnitTypes}
              options={SLOT_OPTIONS.map((opt) => ({
                value: opt.value,
                label: (
                  <span>
                    {opt.icon}
                    {opt.label}
                  </span>
                ),
              }))}
            />
            {!unitTypes.length && (
              <Typography.Text type="danger" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>
                Select at least one slot.
              </Typography.Text>
            )}
          </div>
          {facetsLoading && (
            <Space size="small" style={{ alignSelf: 'flex-end', paddingBottom: 6 }}>
              <Spin size="small" />
              <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
                Loading field values...
              </Typography.Text>
            </Space>
          )}
        </Space>

        <div className="eligibility-qb-wrap">
          <div className="eligibility-qb queryBuilder-branches queryBuilder-responsive">
            <QueryBuilder
              fields={ELIGIBILITY_FIELDS}
              query={query}
              onQueryChange={setQuery}
              getValueEditorType={getValueEditorType}
              getValues={getValues}
              controlElements={controlElements}
              translations={QB_TRANSLATIONS}
              listsAsArrays
              showNotToggle
              showCombinatorsBetweenRules
            />
          </div>
        </div>

        <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
          Conditions within a group combine with its AND/OR selector; use <strong>+ Group</strong> to nest logic, and
          <strong> NOT</strong> to invert a whole group.
          {!hasConditions && ' Add at least one condition to save this rule set.'}
        </Typography.Text>
      </Space>
    </Modal>
  );
};

export default EligibilityRuleSetEditor;
